import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import { officeSourceId } from "./office-source";
import axios, { endpoints, fetcher } from "src/utils/axios";
import type {
  FederationAssignment,
  FederationAssignmentCreate,
  FederationAssignmentList,
  FederationAssignmentOptions,
} from "src/types/federation-assignment";

async function assignmentSourcePage(input: Parameters<typeof fetcher>[0]) {
  const row: FederationAssignment = await fetcher(input);
  return {
    items: [row],
    total: 1,
    offset: 0,
    limit: 20,
    can_manage: row.allowed_actions?.includes("revoke") === true,
  };
}

export function useGetFederationAssignments(
  providerId: number,
  enabled: boolean,
  offset = 0,
) {
  const { user } = useAuthContext();
  const source = officeSourceId("assignments", providerId);
  return useSWR<FederationAssignmentList>(
    providerId > 0 && enabled && user?.id
      ? [
          `${
            source
              ? endpoints.provider.federation.assignment(providerId, source)
              : endpoints.provider.federation.assignments(providerId)
          }#actor-${user.id}`,
          { params: { offset, limit: 20 } },
        ]
      : null,
    source ? assignmentSourcePage : fetcher,
    { keepPreviousData: false },
  );
}

export function useFederationAssignmentOptions(
  providerId: number,
  enabled: boolean,
  search = "",
  roleSearch = "",
  tournamentSearch = "",
) {
  return useSWR<FederationAssignmentOptions>(
    providerId > 0 && enabled
      ? [
          `${endpoints.provider.federation.assignments(providerId)}/options`,
          {
            params: {
              search,
              role_search: roleSearch,
              tournament_search: tournamentSearch,
            },
          },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

export async function createFederationAssignment(
  providerId: number,
  payload: FederationAssignmentCreate,
) {
  const { data } = await axios.post<FederationAssignment>(
    endpoints.provider.federation.assignments(providerId),
    payload,
  );
  return data;
}

export async function revokeFederationAssignment(
  providerId: number,
  row: FederationAssignment,
) {
  const { data } = await axios.post<FederationAssignment>(
    `${endpoints.provider.federation.assignment(providerId, row.id)}/revoke`,
    { expected_revision: row.revision },
  );
  return data;
}
